import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Spinner, Text, Caption1, mergeClasses } from '@fluentui/react-components';
import { connector, Props, useStyles } from './QueueDisplayList.Functions';
import './QueueDisplayList.Styles.css';
import { Desktop20Regular, Delete16Regular, Speaker216Regular, Add16Regular } from '@fluentui/react-icons';
import QueueDisplayFormComponent from '../QueueDisplay.Form/QueueDisplayForm.Component';
import QueueDisplayRegistrationComponent from '../QueueDisplay.Registration/QueueDisplayRegistration.Component';
import ConfirmationModalComponent from '../../../../commons/component/ConfirmationModal/ConformationModal.Component';

const QueueDisplayListComponent = ({ sectorId, displays, isLoading, failOnLoading, error, GetQueueDisplayScreens, CrearLlamado, DeleteQueueDisplayScreen }: Props) => {
  const styles = useStyles();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDisplayId, setEditingDisplayId] = useState<number | undefined>(undefined);
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);

  useEffect(() => {
    GetQueueDisplayScreens();
  }, [GetQueueDisplayScreens, sectorId]);

  const openCreate = useCallback(() => {
    setEditingDisplayId(undefined);
    setIsFormOpen(true);
  }, []);

  const openRegistration = useCallback(() => {
    setIsRegistrationOpen(true);
  }, []);

  const openEdit = useCallback((id?: number) => {
    setEditingDisplayId(id);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingDisplayId(undefined);
  }, []);

  const closeRegistration = useCallback(() => {
    setIsRegistrationOpen(false);
  }, []);

  const filtered = useMemo(() => {
    if (sectorId === undefined || sectorId === null) return displays || [];
    return (displays || []).filter((d) => d.sectorId === sectorId);
  }, [displays, sectorId]);

  const headerSubtitle = useMemo(() => {
    if (filtered.length === 0) return 'Pantallas de llamado del turnero';
    const suffix = filtered.length === 1 ? '' : 's';
    return `${filtered.length} display${suffix} configurado${suffix}`;
  }, [filtered.length]);

  const renderContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="queue-display-loading">
          <Spinner label="Cargando displays" />
        </div>
      );
    }

    if (failOnLoading) {
      return (
        <div className={styles.error}>
          <Text>{error?.ErrorMessage || 'No se pudieron cargar los displays'}</Text>
        </div>
      );
    }

    if (filtered.length === 0) {
      return (
        <div className={styles.empty}>
          <Desktop20Regular style={{ width: 28, height: 28, color: 'var(--colorNeutralForeground4)' }} />
          <Text weight="semibold">Sin displays registrados</Text>
          <Caption1 style={{ color: 'var(--colorNeutralForeground3)' }}>
            {sectorId ? 'Aún no hay displays en este sector' : 'Cree un display o registre uno existente'}
          </Caption1>
        </div>
      );
    }

    return (
      <div className={styles.grid}>
        {filtered.map((d) => (
          <div
            key={d.id ?? d.registrationNumber ?? d.name}
            className={mergeClasses(styles.card, d.isActive ? styles.cardActive : '', 'queue-display-card')}
            role="button"
            tabIndex={0}
            onClick={() => openEdit(d.id)}
            onKeyDown={(evt) => {
              if (evt.key === 'Enter' || evt.key === ' ') {
                evt.preventDefault();
                openEdit(d.id);
              }
            }}
          >
            <div className={styles.cardHeader}>
              <span className={styles.iconWrap}>
                <Desktop20Regular className={styles.icon} />
              </span>
              <span className={styles.name} title={d.name || d.registrationNumber || 'Display'}>
                {d.name || d.registrationNumber || 'Display'}
              </span>
            </div>
            <span className={styles.description} title={d.description || ''}>
              {d.description || 'Sin descripción'}
            </span>
            <div className={styles.cardFooter}>
              <Badge
                appearance="tint"
                color={d.isActive ? 'success' : 'subtle'}
                size="small"
              >
                {d.isActive ? 'Activo' : 'Inactivo'}
              </Badge>
              {d.registrationNumber && (
                <span className={styles.regNumber}>#{d.registrationNumber}</span>
              )}
            </div>
            <div
              className={mergeClasses(styles.cardActions, 'queue-display-card-actions')}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                appearance="subtle"
                icon={<Speaker216Regular />}
                title="Probar llamado"
                onClick={(e) => {
                  e.stopPropagation();
                  CrearLlamado({ nombrePaciente: 'Prueba de display', medicoId: d.id?.toString() ?? '' });
                }}
              />
              <ConfirmationModalComponent
                buttonApparence="subtle"
                buttonShape="square"
                buttonTitle=""
                buttonIcon={<Delete16Regular />}
                title="Eliminar display"
                message={`¿Desea eliminar el display ${d.name || d.registrationNumber || ''}?`}
                onConfirm={() => {
                  if (d.id) {
                    DeleteQueueDisplayScreen(d.id);
                  }
                }}
                onDismiss={() => {}}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }, [isLoading, failOnLoading, filtered, styles, error, openEdit, CrearLlamado, DeleteQueueDisplayScreen, sectorId]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Desktop20Regular style={{ color: '#0f6cbd', flexShrink: 0 }} />
            <Text weight="semibold" size={400}>Displays</Text>
          </div>
          <Caption1 style={{ color: 'var(--colorNeutralForeground3)' }}>
            {headerSubtitle}
          </Caption1>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          <Button size="medium" appearance="secondary" onClick={openRegistration}>
            Registrar
          </Button>
          <Button size="medium" appearance="primary" icon={<Add16Regular />} onClick={openCreate}>
            Nuevo display
          </Button>
        </div>
      </div>

      {renderContent}

      <QueueDisplayFormComponent
        isOpen={isFormOpen}
        onClose={closeForm}
        displayId={editingDisplayId}
        defaultSectorId={sectorId}
      />

      <QueueDisplayRegistrationComponent
        isOpen={isRegistrationOpen}
        onClose={closeRegistration}
        defaultSectorId={sectorId}
      />
    </div>
  );
};

export default connector(QueueDisplayListComponent as any);
